import { VyTextField, type VyTextFieldProps } from '@vy/ui-design-system';
import { isEmptyString } from '@vy/utils';
import { Controller, type FieldPath, type FieldValues } from 'react-hook-form';
import { useFieldController } from './useFieldController';

/** Empty / non-numeric input -> undefined; otherwise the parsed number. */
export function parseOptionalNumber(raw: string): number | undefined {
  if (isEmptyString(raw)) {
    return undefined;
  }
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? undefined : parsed;
}

/** Like parseOptionalNumber, but clamps negatives up to 0. */
export function parseNonNegativeNumber(raw: string): number | undefined {
  const parsed = parseOptionalNumber(raw);
  return parsed === undefined ? undefined : Math.max(parsed, 0);
}

export type VyNumberFieldFieldProps<TForm extends FieldValues> = Omit<
  VyTextFieldProps,
  'value' | 'onChange' | 'onBlur' | 'error' | 'helperText' | 'inputRef' | 'type'
> & {
  name: FieldPath<TForm>;
  /** Hint shown below the field when no error message is present. */
  helperText?: string;
  /** Maps the raw input string to the stored value. Defaults to parseOptionalNumber. */
  parse?: (raw: string) => number | undefined;
};

export function VyNumberFieldField<TForm extends FieldValues>({
  name,
  helperText,
  parse = parseOptionalNumber,
  ...rest
}: VyNumberFieldFieldProps<TForm>) {
  const { control, message } = useFieldController<TForm>(name);
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <VyTextField
          {...rest}
          type="number"
          value={field.value ?? ''}
          onChange={(event) => field.onChange(parse(event.target.value))}
          onBlur={field.onBlur}
          inputRef={field.ref}
          error={Boolean(message)}
          helperText={message ?? helperText}
        />
      )}
    />
  );
}
